import { bindActionCreators } from "@reduxjs/toolkit";
import { connect, useSelector } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import usePagination from "../../hooks/usePagination";
import productActions from "../../store/actions/items/item.actions";
import { useEffect, useState } from "react";
import ItemCardComponent from "../../components/items/itemCard/ItemCardComponent";
import FilterAndSortProductsComponent from "../../components/items/filterAndSortComponents/FilterAndSortProductsComponent";
import "../../styles/pages/items/itemListPageComponent.scss";
import CategoryNavigationComponent from "../../components/categories/CategoryNavigationComponent";
import useDelayedFlag from "../../hooks/useDelayedFlag";
import useDebounce from "../../hooks/useDebounce";
import { SkeletonCardGrid } from "../../components/general/skeleton";

export const ItemListPageComponent = ({ productActions, categoryReducer }) => {
  const categories = useSelector((state) => state.categoryReducer.categories?.items);
  const navigate = useNavigate();
  const { categoryId, itemGroupId } = useParams();
  const location = useLocation();
  const [categoryDetails, setCategoryDetails] = useState();
  const [itemGroupDetails, setItemGroupDetails] = useState();

  const [searchTerm, setSearchTerm] = useState("");

  // 0022: debounce ONLY the value that feeds the request (queryKey + SearchTerm).
  // 350ms while there is text, 0ms (instant) when empty so clearing snaps back to
  // the default view. The visible input and the page title stay on the instant
  // `searchTerm` below — typed text is never gated by the debounce.
  const debouncedSearchTerm = useDebounce(searchTerm, searchTerm ? 350 : 0);

  // Extract search term from query parameters if on the search page
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const searchValue = queryParams.get("query");
    setSearchTerm(searchValue || "");
  }, [location.search]);

  useEffect(() => {
    if (!searchTerm) {
      fetchCategoryAndItemGroup();
    }
  }, [categoryId, itemGroupId, categoryReducer, searchTerm]);

  const fetchCategoryAndItemGroup = () => {
    const selectedCategory = categories?.find((c) => c.id === categoryId);
    setCategoryDetails(selectedCategory);

    const selectedItemGroup = selectedCategory?.itemGroups?.find((g) => g.id === itemGroupId);
    setItemGroupDetails(selectedItemGroup);
  };

  const {
    items: products,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    totalCount,
  } = usePagination({
    queryKey: ["categoryId", categoryId, "itemGroupId", itemGroupId, "searchTerm", debouncedSearchTerm],
    fetchFunction: async (pageParam) => {
      const params = {
        pageNumber: pageParam,
        categoryId,
        itemGroupId,
        SearchTerm: debouncedSearchTerm, // 0022: debounced value drives the request
      };
      const response = await productActions.getItems(params);
      return response; // Ensure this matches the expected format
    },
  });

  // 0018: skeleton card grid on the INITIAL load only. usePagination.isLoading is false once
  // the page is cached (default 60s staleTime), and the ~120ms gate means a fast/cached open
  // shows products with no skeleton flash. The InfiniteScroll "load more" loader is untouched.
  const showSkeleton = useDelayedFlag(isLoading);

  const getProductCardElement = (product) => {
    const productCardProps = { product };
    return <ItemCardComponent key={product.id} props={productCardProps} />;
  };

  const getNoItemsElement = () => (
    <div className="noItemsContainer">
      <h2>לא נמצאו פריטים</h2>
      <p>נסה לשנות את הקריטריונים לחיפוש או חזור לעמוד הראשי.</p>
      <button onClick={() => navigate("/")}>חזור לעמוד הראשי</button>
    </div>
  );

  const getProductListEelement = () => {
    return (
      <InfiniteScroll
        dataLength={products.length}
        next={fetchNextPage}
        hasMore={hasNextPage} // Prevent InfiniteScroll while forcing viewport fill
        scrollableTarget="productsListContainer"
        loader={<div>טוען פריטים נוספים...</div>}>
        <div className="productListContainer">{products.map((product) => getProductCardElement(product))}</div>
      </InfiniteScroll>
    );
  };

  return (
    <div className="productsListContainer">
      {/* Display dynamic title */}
      <div className="pageTitleContainer">
        {searchTerm ? (
          <h1>תוצאות חיפוש עבור: {searchTerm}</h1>
        ) : (
          <h1>
            {categoryDetails?.categoryName}
            {itemGroupDetails && ` - ${itemGroupDetails.itemGroupName}`}
          </h1>
        )}
      </div>
      <FilterAndSortProductsComponent totalCount={totalCount} />
      {/* 0018: while the first page is loading, show a skeleton grid (or nothing for the first
          ~120ms) — never the "no items" message, which previously flashed during every load. */}
      {isLoading
        ? showSkeleton && <SkeletonCardGrid count={8} />
        : products && products.length > 0
        ? getProductListEelement()
        : getNoItemsElement()}

      <CategoryNavigationComponent props={{ categoryId, itemGroupId }} />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    categoryReducer: state.categoryReducer,
  };
};

const mapDispatchToProps = (dispatch) => ({
  productActions: bindActionCreators(productActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ItemListPageComponent);
